import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Image, ExternalLink } from 'lucide-react';

const projects = [
  {
    id: 1,
    title: 'E-Commerce Redesign',
    category: 'E-Commerce',
    image: 'https://images.unsplash.com/photo-1547658719-da2b51169166'
  },
  {
    id: 2,
    title: 'SaaS Dashboard',
    category: 'Web App',
    image: 'https://images.unsplash.com/photo-1593720213428-28a5b9e94613'
  },
  {
    id: 3,
    title: 'Corporate Website',
    category: 'Website',
    image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085'
  },
  {
    id: 4,
    title: 'Mobile App UI',
    category: 'UI/UX',
    image: 'https://images.unsplash.com/photo-1541462608143-67571c6738dd'
  },
  {
    id: 5,
    title: 'Brand Identity',
    category: 'Branding',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40'
  },
  {
    id: 6,
    title: 'Marketing Campaign',
    category: 'Marketing',
    image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113'
  }
];

export default function GalleryPage() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-muted/50 to-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge variant="secondary" className="mb-6">
              <Image className="h-4 w-4 mr-2" />
              Project Gallery
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-6">
              Our{' '}
              <span className="gradient-text">Work</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              A visual showcase of our latest projects and creative work.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer"
              >
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <Badge variant="secondary" className="w-fit mb-2 bg-primary/20 text-primary-foreground border-none">
                    {project.category}
                  </Badge>
                  <h3 className="text-xl font-display font-bold text-background">
                    {project.title}
                  </h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 gradient-primary">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-6">
              Like What You See?
            </h2>
            <p className="text-primary-foreground/80 mb-8">
              Let's create something amazing together.
            </p>
            <Button variant="premium" size="lg" asChild className="bg-background text-foreground hover:bg-background/90">
              <Link to="/get-started">
                Start Your Project
                <ArrowRight className="h-5 w-5 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
